// src/pages/ProfileSettings.tsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  User,
  Mail,
  MapPin,
  Github,
  Linkedin,
  Twitter,
  Globe,
  Save,
  X,
  Plus,
  Trash2,
  Edit2,
  ArrowLeft
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

// Professional profile images for ANALYSTS only
const PROFILE_IMAGES: Record<string, string> = {
  'admin@vettandventure.com': 'https://images.pexels.com/photos/3778876/pexels-photo-3778876.jpeg?auto=compress&cs=tinysrgb&w=200&h=200&fit=crop',
  'analyst@vett.com': 'https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&w=200&h=200&fit=crop',
  'analyst2@vett.com': 'https://images.pexels.com/photos/762020/pexels-photo-762020.jpeg?auto=compress&cs=tinysrgb&w=200&h=200&fit=crop',
  'senior@vett.com': 'https://images.pexels.com/photos/697509/pexels-photo-697509.jpeg?auto=compress&cs=tinysrgb&w=200&h=200&fit=crop',
  'demo.problembank@vett.com': 'https://images.pexels.com/photos/2380794/pexels-photo-2380794.jpeg?auto=compress&cs=tinysrgb&w=200&h=200&fit=crop',
  'demo.business@vett.com': 'https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&w=200&h=200&fit=crop',
  'demo.senior@vett.com': 'https://images.pexels.com/photos/697509/pexels-photo-697509.jpeg?auto=compress&cs=tinysrgb&w=200&h=200&fit=crop',
};

// Job titles for ANALYSTS only
const JOB_TITLES: Record<string, string> = {
  'admin@vettandventure.com': 'System Administrator',
  'analyst@vett.com': 'Investment Analyst',
  'analyst2@vett.com': 'Climate Tech Analyst',
  'senior@vett.com': 'Senior Investment Analyst',
  'demo.problembank@vett.com': 'Problem Bank Analyst',
  'demo.business@vett.com': 'Business Analyst',
  'demo.senior@vett.com': 'Senior Analyst',
};

// Locations for ANALYSTS only
const LOCATIONS: Record<string, string> = {
  'admin@vettandventure.com': 'New York, NY',
  'analyst@vett.com': 'Boston, MA',
  'analyst2@vett.com': 'Seattle, WA',
  'senior@vett.com': 'Chicago, IL',
  'demo.problembank@vett.com': 'Nairobi, Kenya',
  'demo.business@vett.com': 'Nairobi, Kenya',
  'demo.senior@vett.com': 'Nairobi, Kenya',
};

// Phone numbers for ANALYSTS only
const PHONE_NUMBERS: Record<string, string> = {
  'admin@vettandventure.com': '+1 (212) 555-0100',
  'analyst@vett.com': '+1 (617) 555-0789',
  'analyst2@vett.com': '+1 (206) 555-0321',
  'senior@vett.com': '+1 (312) 555-0654',
  'demo.problembank@vett.com': '+254 700 123 456',
  'demo.business@vett.com': '+254 700 123 457',
  'demo.senior@vett.com': '+254 700 123 458',
};

// Bios for ANALYSTS only
const BIOS: Record<string, string> = {
  'admin@vettandventure.com': 'Experienced investment professional with 15+ years in venture capital. Led 50+ successful investments and managed portfolio worth $2B+.',
  'analyst@vett.com': 'Investment analyst specializing in early-stage FinTech and SaaS startups. CFA Level III candidate.',
  'analyst2@vett.com': 'Climate tech specialist with background in environmental science and impact investing.',
  'senior@vett.com': 'Senior investment professional with deep expertise in due diligence and deal structuring.',
  'demo.problembank@vett.com': 'Problem Bank Analyst focused on identifying and validating industrial problems for solution squads.',
  'demo.business@vett.com': 'Business Analyst specializing in squad management and problem assignment.',
  'demo.senior@vett.com': 'Senior Analyst overseeing problem bank operations and analyst performance.',
};

// Specializations for ANALYSTS only
const SPECIALIZATIONS: Record<string, string[]> = {
  'admin@vettandventure.com': ['Venture Capital', 'Portfolio Management', 'Strategy', 'M&A'],
  'analyst@vett.com': ['FinTech', 'SaaS', 'Financial Modeling', 'Due Diligence'],
  'analyst2@vett.com': ['Climate Tech', 'Impact Investing', 'ESG', 'Clean Energy'],
  'senior@vett.com': ['Due Diligence', 'Deal Structuring', 'Mentorship', 'Valuation'],
  'demo.problembank@vett.com': ['Problem Validation', 'Industry Research', 'Data Analysis', 'Root Cause Analysis'],
  'demo.business@vett.com': ['Squad Management', 'Problem Assignment', 'Market Analysis', 'Solution Validation'],
  'demo.senior@vett.com': ['Team Leadership', 'Strategic Planning', 'Performance Management', 'Quality Assurance'],
};

export default function ProfileSettings() {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');
  const [imageError, setImageError] = useState(false);
  const [newSpecialization, setNewSpecialization] = useState('');
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Show loading spinner while auth is loading
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
      </div>
    );
  }

  // Redirect if no user
  if (!user) {
    navigate('/analyst-login');
    return null;
  }

  // CRITICAL: If user is a founder, redirect to founder profile page
  if (user.role === 'founder') {
    navigate('/profile');
    return null;
  }

  // Get user-specific data based on email
  const userEmail = user?.email || '';
  const defaultImage = PROFILE_IMAGES[userEmail] || null;
  const defaultTitle = JOB_TITLES[userEmail] || (user?.role === 'senior_analyst' ? 'Senior Analyst' : 'Analyst');
  const defaultLocation = LOCATIONS[userEmail] || 'Remote';
  const defaultPhone = PHONE_NUMBERS[userEmail] || '+1 (555) 000-0000';
  const defaultBio = BIOS[userEmail] || 'Team member at Vett & Venture.';
  const defaultSpecializations = SPECIALIZATIONS[userEmail] || ['General'];

  // Load profile from localStorage or use defaults
  const loadProfile = () => {
    if (!user) return null;
    
    const saved = localStorage.getItem(`profile_${user.id}`);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Error parsing saved profile', e);
      }
    }
    return null;
  };

  // Profile state - initialize from localStorage or defaults
  const [profile, setProfile] = useState(() => {
    const saved = loadProfile();
    if (saved) {
      setProfileLoaded(true);
      return saved;
    }
    
    setProfileLoaded(true);
    return {
      fullName: user?.name || '',
      email: user?.email || '',
      phone: defaultPhone,
      location: defaultLocation,
      bio: defaultBio,
      title: defaultTitle,
      education: 'MBA, Stanford University\nBS Computer Science, MIT',
      specializations: defaultSpecializations,
      yearsExperience: 10,
      linkedin: 'https://linkedin.com/in/username',
      twitter: 'https://twitter.com/username',
      github: 'https://github.com/username',
      website: 'https://example.com',
      profileImage: defaultImage,
      experience: [
        {
          role: defaultTitle,
          company: 'Vett & Venture',
          period: '2024 - Present',
          description: 'Current position at Vett & Venture.'
        }
      ]
    };
  });

  // Save to localStorage whenever profile changes
  const saveProfile = () => {
    if (user && profileLoaded) {
      localStorage.setItem(`profile_${user.id}`, JSON.stringify(profile));
    }
  };

  // Reload profile when user changes (login/logout)
  useEffect(() => {
    if (user && user.role !== 'founder') {
      const saved = loadProfile();
      if (saved) {
        setProfile(saved);
      } else {
        // Reset to defaults for new user
        setProfile(prev => ({
          ...prev,
          fullName: user.name || '',
          email: user.email || '',
          title: defaultTitle,
          location: defaultLocation,
          phone: defaultPhone,
          bio: defaultBio,
          specializations: defaultSpecializations,
          profileImage: defaultImage,
        }));
      }
    }
  }, [user]);

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getRoleBadgeColor = (role: string) => {
    switch(role) {
      case 'senior_analyst':
        return 'bg-purple-600 text-white';
      case 'analyst':
        return 'bg-blue-600 text-white';
      default:
        return 'bg-gray-600 text-white';
    }
  };

  const handleSave = async () => {
    setSaving(true);
    saveProfile();
    await new Promise(resolve => setTimeout(resolve, 500));
    toast({
      title: "Profile updated",
      description: "Your profile has been saved successfully.",
    });
    setIsEditing(false);
    setSaving(false);
  };

  const handleCancel = () => {
    const saved = loadProfile();
    if (saved) {
      setProfile(saved);
    }
    setIsEditing(false);
  };

  const addSpecialization = () => {
    if (newSpecialization.trim() && !profile.specializations.includes(newSpecialization.trim())) {
      setProfile({
        ...profile,
        specializations: [...profile.specializations, newSpecialization.trim()]
      });
      setNewSpecialization('');
    }
  };

  const removeSpecialization = (index: number) => {
    setProfile({
      ...profile,
      specializations: profile.specializations.filter((_, i) => i !== index)
    });
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfile({
          ...profile,
          profileImage: reader.result as string
        });
        setImageError(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const renderField = (label: string, value: string, field: string, type: 'input' | 'textarea' = 'input') => {
    if (isEditing) {
      if (type === 'textarea') {
        return (
          <div className="space-y-2">
            <Label>{label}</Label>
            <Textarea
              value={value}
              onChange={(e) => setProfile({ ...profile, [field]: e.target.value })}
              className="min-h-[80px]"
            />
          </div>
        );
      }
      return (
        <div className="space-y-2">
          <Label>{label}</Label>
          <Input
            value={value}
            onChange={(e) => setProfile({ ...profile, [field]: e.target.value })}
          />
        </div>
      );
    }
    
    return (
      <div className="space-y-1">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm">{value || 'Not specified'}</p>
      </div>
    );
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto px-4 py-6">
      {/* Header with Back Button */}
      <div className="flex items-center justify-between">
        <div>
          <Button 
            variant="ghost" 
            onClick={() => navigate('/analyst-dashboard')} 
            className="gap-2 mb-4 -ml-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Button>
          <h1 className="text-3xl font-bold">Profile Settings</h1>
          <p className="text-muted-foreground mt-1">
            Manage your professional profile and public information
          </p>
        </div>
        <div className="flex gap-2">
          {!isEditing ? (
            <Button onClick={() => setIsEditing(true)}>
              <Edit2 className="h-4 w-4 mr-2" />
              Edit Profile
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={handleCancel}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Profile Preview Card */}
      <Card className="overflow-hidden">
        <div className="h-20 bg-gradient-to-r from-blue-600 to-purple-600" />
        <CardContent className="relative pt-0">
          <div className="flex flex-col md:flex-row gap-6 -mt-10">
            {/* PROFILE PHOTO */}
            <div className="flex-shrink-0">
              <div className="relative">
                <div className="h-24 w-24 rounded-full overflow-hidden border-4 border-white shadow-xl bg-white">
                  {profile.profileImage && !imageError ? (
                    <img 
                      src={profile.profileImage}
                      alt={profile.fullName}
                      className="h-full w-full object-cover"
                      onError={() => setImageError(true)}
                    />
                  ) : (
                    <div className={`h-full w-full flex items-center justify-center text-2xl font-bold ${getRoleBadgeColor(user?.role || 'analyst')}`}>
                      {getInitials(profile.fullName)}
                    </div>
                  )}
                </div>
                {isEditing && (
                  <label 
                    htmlFor="profile-image" 
                    className="absolute bottom-0 right-0 bg-blue-600 text-white p-1.5 rounded-full cursor-pointer hover:bg-blue-700 transition-colors shadow-lg"
                  >
                    <User className="h-4 w-4" />
                  </label>
                )}
                <input
                  type="file"
                  id="profile-image"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageUpload}
                />
              </div>
            </div>
            
            {/* Profile Info */}
            <div className="flex-1 pt-4">
              <h2 className="text-2xl font-bold">{profile.fullName}</h2>
              <p className="text-muted-foreground">{profile.title}</p>
              <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                {profile.location}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Tabs - Only show tabs when editing, otherwise show condensed view */}
      {isEditing ? (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4 lg:w-[600px]">
            <TabsTrigger value="personal">Personal</TabsTrigger>
            <TabsTrigger value="professional">Professional</TabsTrigger>
            <TabsTrigger value="experience">Experience</TabsTrigger>
            <TabsTrigger value="social">Social</TabsTrigger>
          </TabsList>

          {/* Personal Info Tab */}
          <TabsContent value="personal" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Personal Information</CardTitle>
                <CardDescription>
                  Basic information about yourself
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Full Name</Label>
                    <Input
                      value={profile.fullName}
                      onChange={(e) => setProfile({ ...profile, fullName: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={profile.email}
                      onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={profile.phone}
                      onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Location</Label>
                    <Input
                      value={profile.location}
                      onChange={(e) => setProfile({ ...profile, location: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Professional Title</Label>
                  <Input
                    value={profile.title}
                    onChange={(e) => setProfile({ ...profile, title: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Bio</Label>
                  <Textarea
                    value={profile.bio}
                    onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                    className="min-h-[100px]"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Professional Info Tab */}
          <TabsContent value="professional" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Professional Background</CardTitle>
                <CardDescription>
                  Your education, skills, and experience
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Education</Label>
                  <Textarea
                    value={profile.education}
                    onChange={(e) => setProfile({ ...profile, education: e.target.value })}
                    className="min-h-[80px]"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Years of Experience</Label>
                  <Input
                    type="number"
                    value={profile.yearsExperience}
                    onChange={(e) => setProfile({ ...profile, yearsExperience: parseInt(e.target.value) || 0 })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Specializations</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newSpecialization}
                      onChange={(e) => setNewSpecialization(e.target.value)}
                      placeholder="Add a specialization"
                      onKeyPress={(e) => e.key === 'Enter' && addSpecialization()}
                    />
                    <Button type="button" onClick={addSpecialization}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {profile.specializations.map((spec, index) => (
                      <Badge key={index} variant="secondary" className="px-3 py-1">
                        {spec}
                        <button
                          onClick={() => removeSpecialization(index)}
                          className="ml-2 hover:text-red-500"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Experience Tab */}
          <TabsContent value="experience" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Work Experience</CardTitle>
                <CardDescription>
                  Your work history
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {profile.experience.map((exp, index) => (
                  <div key={index} className="flex justify-between items-start p-4 border rounded-lg">
                    <div>
                      <h4 className="font-semibold">{exp.role}</h4>
                      <p className="text-sm text-muted-foreground">{exp.company}</p>
                      <p className="text-xs text-muted-foreground mt-1">{exp.period}</p>
                      <p className="text-sm mt-2">{exp.description}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" className="w-full">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Experience
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Social Links Tab */}
          <TabsContent value="social" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Social Links</CardTitle>
                <CardDescription>
                  Connect your professional social media accounts
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Linkedin className="h-4 w-4 text-blue-600" />
                    LinkedIn
                  </Label>
                  <Input
                    value={profile.linkedin}
                    onChange={(e) => setProfile({ ...profile, linkedin: e.target.value })}
                    placeholder="https://linkedin.com/in/username"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Twitter className="h-4 w-4 text-blue-400" />
                    Twitter/X
                  </Label>
                  <Input
                    value={profile.twitter}
                    onChange={(e) => setProfile({ ...profile, twitter: e.target.value })}
                    placeholder="https://twitter.com/username"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Github className="h-4 w-4" />
                    GitHub
                  </Label>
                  <Input
                    value={profile.github}
                    onChange={(e) => setProfile({ ...profile, github: e.target.value })}
                    placeholder="https://github.com/username"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    Website
                  </Label>
                  <Input
                    value={profile.website}
                    onChange={(e) => setProfile({ ...profile, website: e.target.value })}
                    placeholder="https://yourwebsite.com"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      ) : (
        /* View Mode - Display profile information in a clean, read-only layout */
        <div className="space-y-4">
          {/* Personal Info Card */}
          <Card>
            <CardHeader>
              <CardTitle>Personal Information</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {renderField('Full Name', profile.fullName, 'fullName')}
              {renderField('Email', profile.email, 'email')}
              {renderField('Phone', profile.phone, 'phone')}
              {renderField('Location', profile.location, 'location')}
              {renderField('Professional Title', profile.title, 'title')}
              {renderField('Bio', profile.bio, 'bio', 'textarea')}
            </CardContent>
          </Card>

          {/* Professional Background Card */}
          <Card>
            <CardHeader>
              <CardTitle>Professional Background</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {renderField('Education', profile.education, 'education', 'textarea')}
              {renderField('Years of Experience', profile.yearsExperience.toString(), 'yearsExperience')}
              
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">Specializations</p>
                <div className="flex flex-wrap gap-2">
                  {profile.specializations.map((spec, index) => (
                    <Badge key={index} variant="secondary">
                      {spec}
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Experience Card */}
          <Card>
            <CardHeader>
              <CardTitle>Work Experience</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {profile.experience.map((exp, index) => (
                <div key={index} className="p-3 border rounded-lg">
                  <h4 className="font-semibold">{exp.role}</h4>
                  <p className="text-sm text-muted-foreground">{exp.company}</p>
                  <p className="text-xs text-muted-foreground mt-1">{exp.period}</p>
                  <p className="text-sm mt-2">{exp.description}</p>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Social Links Card */}
          <Card>
            <CardHeader>
              <CardTitle>Social Links</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {renderField('LinkedIn', profile.linkedin, 'linkedin')}
              {renderField('Twitter/X', profile.twitter, 'twitter')}
              {renderField('GitHub', profile.github, 'github')}
              {renderField('Website', profile.website, 'website')}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}